/* Roles — handover §14 step 10.
 *
 * The one thing this screen has to get across is that **it is not the security
 * boundary**. It draws the matrix; every restriction that matters is a real
 * SharePoint permission on a real list, granted to a real group. A screen that
 * looked like an access-control panel would invite somebody to "fix" access by
 * changing something here, and nothing here changes anything.
 *
 * So it is read-only and says why, and it names the Entra group behind each
 * role — because that group is where the change actually gets made.
 *
 * The four special permissions sit apart from the read/write grid because each
 * is an escalation rather than a degree of access (§6.3). "Manage API keys" is
 * admin-equivalent by another route, and burying it in a column of ticks would
 * hide that.
 */

import { useQuery } from '@tanstack/react-query';
import type { IModule, IRoleDefinition } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Badge, EmptyState, LoadError, Panel, TableSkeleton } from '../ui';
import s from './screens.module.scss';

const ACCESS_TONE = { write: 'ok', read: 'info', none: 'muted' } as const;
const ACCESS_LABEL = { write: 'Write', read: 'Read', none: '—' } as const;

const SPECIAL: { key: keyof IRoleDefinition['special']; label: string; why: string }[] = [
  { key: 'export', label: 'Export', why: 'Takes data out of the boundary entirely.' },
  {
    key: 'manageRecordTypes',
    label: 'Manage record types',
    why: 'Changes what every custom register holds, for everybody.',
  },
  {
    key: 'manageApiKeys',
    label: 'Manage API keys',
    why: 'Admin-equivalent by another route — a key can do what its holder can.',
  },
];

export function RolesScreen() {
  const svc = useServices();

  const roles = useQuery({ queryKey: qk.roles, queryFn: () => svc.access.roles() });
  const modules = useQuery({ queryKey: qk.modules, queryFn: () => svc.access.modules() });

  const enabled: IModule[] = (modules.data ?? []).filter((m) => m.enabled);

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Roles</h1>
          <p>
            What each group can do. A picture of the permissions — the restrictions themselves
            are SharePoint permissions on the lists.
          </p>
        </div>
      </div>

      {(roles.isError || modules.isError) && (
        <LoadError error={roles.error ?? modules.error} what="roles" />
      )}

      <div className={s.ownedNote}>
        <i className="ti ti-shield-lock" aria-hidden />
        <span>
          Nothing on this screen is editable, and that is deliberate. Access is changed by moving
          somebody in or out of the Entra group named against each role below — the app only ever
          reads the result. A role changed here and not there would be a lie the app told itself.
        </span>
      </div>

      {roles.isLoading || modules.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={5} />
        </Panel>
      ) : (roles.data ?? []).length === 0 ? (
        <Panel flush>
          <EmptyState icon="ti-users-group" title="No roles defined" />
        </Panel>
      ) : (
        <div className={s.stack}>
          <Panel title="Who can do what" sub="By module, across every role" flush>
            <div className={s.matrixWrap}>
              <table className={s.matrix}>
                <thead>
                  <tr>
                    <th>Module</th>
                    {(roles.data ?? []).map((r) => (
                      <th key={r.role}>{r.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {enabled.map((m) => (
                    <tr key={m.id}>
                      <th scope="row">
                        <i className={`ti ${m.icon}`} aria-hidden /> {m.label}
                      </th>
                      {(roles.data ?? []).map((r) => {
                        const access = r.matrix[m.id] ?? 'none';
                        return (
                          <td key={r.role}>
                            <Badge tone={ACCESS_TONE[access]}>{ACCESS_LABEL[access]}</Badge>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="Special permissions"
            sub="Escalations, not degrees of access"
            flush
          >
            <div className={s.matrixWrap}>
              <table className={s.matrix}>
                <thead>
                  <tr>
                    <th>Permission</th>
                    {(roles.data ?? []).map((r) => (
                      <th key={r.role}>{r.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SPECIAL.map((sp) => (
                    <tr key={sp.key}>
                      <th scope="row">
                        {sp.label}
                        <div className={s.cellSub}>{sp.why}</div>
                      </th>
                      {(roles.data ?? []).map((r) => (
                        <td key={r.role}>
                          <Badge tone={r.special[sp.key] ? 'bad' : 'muted'}>
                            {r.special[sp.key] ? 'Yes' : '—'}
                          </Badge>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {(roles.data ?? []).map((r) => (
            <Panel
              key={r.role}
              title={r.label}
              sub={r.description}
              actions={<Badge tone="info">{r.group}</Badge>}
            >
              <div className={s.hint}>
                Add or remove people in <span className={s.mono}>{r.group}</span> to change who
                holds this role. Membership must be direct — a group nested inside it grants
                nothing.
              </div>
            </Panel>
          ))}
        </div>
      )}
    </>
  );
}
